import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export type Notification = {
  id: string;
  destination_profile_id: string;
  source_profile: {
    id: string;
    first_name: string;
    avatar_url: string | null;
  } | null;
  type: string;
  payload: Record<string, unknown>;
  read_at: string | null;
  is_email_sent: boolean;
  created_at: string;
};

interface NotificationState {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  pageSize: number;
  unreadCount: number;
  allianceNotificationsCount: number;
  quoteRequestNotificationsCount: number;
  emailOnlyTypes: string[];
  emailOnlyTypesLoaded: boolean;
  fetchNotifications: (pageSize?: number) => Promise<void>;
  fetchMore: () => Promise<void>;
  markNotificationsAsRead: (notificationIds: string[]) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  fetchEmailOnlyTypes: () => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  loading: false,
  error: null,
  hasMore: true,
  pageSize: 10,
  unreadCount: 0,
  allianceNotificationsCount: 0,
  quoteRequestNotificationsCount: 0,
  emailOnlyTypes: [],
  emailOnlyTypesLoaded: false,

  fetchEmailOnlyTypes: async () => {
    const { data, error } = await supabase.rpc('get_email_only_notification_types');
    if (error) {
      console.error('Failed to fetch email-only types:', error.message);
      set({ emailOnlyTypesLoaded: true });
      return;
    }
    set({ emailOnlyTypes: data ?? [], emailOnlyTypesLoaded: true });
  },

  fetchNotifications: async (pageSize?: number) => {
    if (get().loading) return;

    set({ loading: true });
    try {
      const limit = pageSize ?? get().pageSize;
      const { emailOnlyTypes } = get();
      const { data, error } = await supabase.rpc('fetch_notifications', {
        p_limit: limit,
        p_offset: 0,
        p_excluded_types: emailOnlyTypes.length > 0 ? emailOnlyTypes : null,
      });

      if (error) throw error;

      const notifications = (data || []) as Notification[];
      set({
        notifications,
        hasMore: notifications.length === limit,
        error: null,
      });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  fetchMore: async () => {
    if (get().loading || !get().hasMore) return;

    set({ loading: true });
    try {
      const offset = get().notifications.length;
      const limit = get().pageSize;
      const { emailOnlyTypes } = get();
      const { data, error } = await supabase.rpc('fetch_notifications', {
        p_limit: limit,
        p_offset: offset,
        p_excluded_types: emailOnlyTypes.length > 0 ? emailOnlyTypes : null,
      });

      if (error) throw error;

      const newNotifications = (data || []) as Notification[];
      set(state => ({
        notifications: [...state.notifications, ...newNotifications],
        hasMore: newNotifications.length === limit,
        error: null,
      }));
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  markNotificationsAsRead: async (notificationIds: string[]) => {
    try {
      if (notificationIds.length === 0) return;

      const { error } = await supabase.rpc('mark_notifications_as_read', {
        p_notification_ids: notificationIds,
      });

      if (error) throw error;
      await get().fetchNotifications();
      await get().fetchUnreadCount();
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },

  fetchUnreadCount: async () => {
    const { data, error } = await supabase.rpc('get_all_notification_counts');

    if (error) {
      console.error(error);
      return;
    }

    set({
      unreadCount: data.unreadCount ?? 0,
      allianceNotificationsCount: data.allianceNotificationsCount ?? 0,
      quoteRequestNotificationsCount: data.quoteRequestNotificationsCount ?? 0,
    });
  },
}));
